import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { takeCaptureReadSlot } from "../../../lib/captureReadSlots";
import { fileRecordVersionKey } from "../../../lib/fileRecordVersion";
import { holdStaticBlockPaint } from "../../../lib/staticBlockPaintGate";
import { useFileRecordText } from "../../../lib/useFileRecordText";
import { cn } from "../../../lib/utils";
import {
  HtmlBlockCardCanvas,
  type HtmlBlockCardCanvasProps,
} from "./HtmlBlockCardCanvas";
import { HtmlCardPlaceholder } from "./HtmlCardPlaceholder";
import { HtmlStaticBlockCard } from "./HtmlStaticBlockCard";

/** Only `kind: "static"` renders in the page; `"app"` and no kind keep the
 *  iframe card. */
export type HtmlBlockCardSwitchProps = HtmlBlockCardCanvasProps;

/**
 * Which card a block gets, decided by the record's declared kind.
 *
 * Until the row is in (no url) the kind is unknown, so neither card mounts:
 * mounting the iframe card and then swapping would build a document for a
 * block that never needed one. The plate shown meanwhile is the one either
 * card would show. A static block's body is read here, through the same
 * keyed query the iframe card reads.
 */
export function HtmlBlockCardSwitch({
  kind,
  ...props
}: HtmlBlockCardSwitchProps) {
  const { url, path, content_hash, updated_at, mountPolicy } = props;
  // A whole-canvas capture shows posters only: no static render, no read.
  const isStatic = kind === "static" && mountPolicy !== "poster";
  const capture = mountPolicy === "capture";
  const [slotted, setSlotted] = useState(!capture);
  const versionKey = fileRecordVersionKey({ path, content_hash, updated_at });
  const { data: body, isError } = useFileRecordText({
    url,
    versionKey,
    enabled: isStatic && slotted,
  });

  const readDone = body != null || isError;
  useEffect(() => {
    if (!capture || !isStatic || !url || readDone) return;
    return takeCaptureReadSlot(() => setSlotted(true));
  }, [capture, isStatic, url, readDone]);

  // The capture's ready signal waits for this card to paint, or to turn out
  // to be an iframe card, which keeps its own poster path.
  const releaseRef = useRef<(() => void) | null>(null);
  useLayoutEffect(() => {
    if (!capture) return;
    const release = holdStaticBlockPaint();
    releaseRef.current = release;
    return () => {
      release();
      releaseRef.current = null;
    };
  }, [capture]);
  const routedToFrame = !!url && !isStatic;
  useEffect(() => {
    if (routedToFrame || isError) releaseRef.current?.();
  }, [routedToFrame, isError]);

  const { onDisplayReady } = props;
  const onStaticDisplayReady = useCallback(() => {
    onDisplayReady?.();
    const release = releaseRef.current;
    if (release) requestAnimationFrame(() => requestAnimationFrame(release));
  }, [onDisplayReady]);

  if (url && !isStatic) return <HtmlBlockCardCanvas {...props} kind={kind} />;

  if (!url || body == null) {
    return (
      <div
        className={cn(
          "group relative h-full w-full overflow-hidden",
          !props.display.chromeless && "rounded-xl",
          props.className,
        )}
        style={{ colorScheme: props.theme ?? "light", ...props.style }}
        onDoubleClick={() => props.onDoubleClick?.()}
      >
        <HtmlCardPlaceholder
          previewImageUrl={
            props.display.chromeless ? null : (props.previewImageUrl ?? null)
          }
          showPlate={!props.display.chromeless}
        />
      </div>
    );
  }

  return (
    <HtmlStaticBlockCard
      body={body}
      display={props.display}
      theme={props.theme}
      transformHtml={props.transformHtml}
      previewImageUrl={props.previewImageUrl}
      selected={props.selected}
      focused={props.focused}
      resizing={props.resizing}
      isInteractive={props.isInteractive}
      islandsEditable={props.islandsEditable}
      contentFrozen={props.contentFrozen}
      editFocusPoint={props.editFocusPoint}
      onRuntimeReady={props.onRuntimeReady}
      onBlockMessage={props.onBlockMessage}
      onNavigate={props.onNavigate}
      clickThrough={props.clickThrough}
      className={props.className}
      style={props.style}
      onDoubleClick={props.onDoubleClick}
      onDisplayReady={onStaticDisplayReady}
    />
  );
}
